import { ActionIcon, Button, Card, Center, Skeleton, Stack, Table, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconNewsOff, IconPlus, IconTrash } from '@tabler/icons-react';
import { PageTitle } from 'components/PageTitle';
import { i18nKeys, useTranslation } from 'locales';
import { upperFirst } from 'lodash-es';
import { useHistory } from 'react-router';
import { isEmpty, times } from 'remeda';
import { useArchiveReport } from 'shared/hooks/use-archive-report';
import { useLoadReports } from 'shared/hooks/use-load-reports';

import { PeriodicityLabel } from '../components';

export const ReportsIndex = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { reports } = useLoadReports();
  const { archiveReport, isArchiveReportLoading } = useArchiveReport();

  const handleArchiveReport = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();

    modals.openConfirmModal({
      centered: true,
      title: t(i18nKeys.REPORTS.SHOW.DELETE_REPORT),
      children: <Text>{t(i18nKeys.REPORTS.SHOW.AYS_DELETE)}</Text>,
      labels: { cancel: t(i18nKeys.CANCEL), confirm: t(i18nKeys.CONFIRM) },
      confirmProps: { color: 'red', loading: isArchiveReportLoading },
      onConfirm: () => archiveReport(id),
      onCancel: modals.closeAll,
    });
  };

  const tableBody = reports
    ? reports.map((report, index) => (
        <Table.Tr
          key={index}
          style={{ cursor: 'pointer' }}
          onClick={() => history.push(`/reports/${report.id}`)}
        >
          <Table.Td>
            <Text c="gray.8" fz={14}>
              {upperFirst(report.name)}
            </Text>
          </Table.Td>
          <Table.Td>
            <PeriodicityLabel type={report.periodicity.type} />
          </Table.Td>
          <Table.Td>
            {report.lastExecutedAt ? (
              report.lastExecutedAt.format('LLLL')
            ) : (
              <Text fz={14} fs="italic" c="dimmed">
                {t(i18nKeys.REPORTS.INDEX.EXECUTION_DATE.NOT_EXECUTED)}
              </Text>
            )}
          </Table.Td>
          <Table.Td>
            <ActionIcon
              onClick={e => handleArchiveReport(e, report.id!)}
              size="input-xs"
              variant="light"
              color="red"
            >
              <IconTrash stroke={1.5} size={16} />
            </ActionIcon>
          </Table.Td>
        </Table.Tr>
      ))
    : times(5, index => (
        <Table.Tr key={index}>
          <Table.Td>
            <Skeleton height={20} w="80%" />
          </Table.Td>
          <Table.Td>
            <Skeleton height={20} w="80%" />
          </Table.Td>
          <Table.Td>
            <Skeleton height={20} w="80%" />
          </Table.Td>
          <Table.Td>
            <Skeleton height={20} w={20} mr={15} />
            <Skeleton height={20} w={20} />
          </Table.Td>
        </Table.Tr>
      ));

  return (
    <>
      <PageTitle>
        {t(i18nKeys.REPORTS.INDEX.TITLE)}
        <PageTitle.Actions>
          <Button
            onClick={() => {
              localStorage.removeItem('persist-report-creation');
              history.push('/reports/new');
            }}
            leftSection={<IconPlus stroke={1.5} />}
          >
            {t(i18nKeys.REPORTS.NEW.ACTION_BUTTON)}
          </Button>
        </PageTitle.Actions>
      </PageTitle>
      <Card radius={8} p={0}>
        {isEmpty(tableBody) ? (
          <Center h={200}>
            <Stack align="center">
              <IconNewsOff size={80} color="gray" />
              <Text>{t(i18nKeys.REPORTS.SHOW.NO_REPORTS_YET)}</Text>
            </Stack>
          </Center>
        ) : (
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t(i18nKeys.REPORTS.INDEX.ROWS.NAME)}</Table.Th>
                <Table.Th>{t(i18nKeys.REPORTS.INDEX.ROWS.PERIODICITY)}</Table.Th>
                <Table.Th>{t(i18nKeys.REPORTS.INDEX.ROWS.EXECUTION_DATE)}</Table.Th>
                <Table.Th>{t(i18nKeys.REPORTS.INDEX.ROWS.ACTIONS)}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isEmpty(tableBody) ? (
                <Center>
                  <Text m="lg">Nope</Text>
                </Center>
              ) : (
                tableBody
              )}
            </Table.Tbody>
          </Table>
        )}
      </Card>
    </>
  );
};
